import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Package, Check, X, Undo2 } from "lucide-react";
import { money } from "@/lib/helpers";
import logoWhite from "@/assets/logo-white.webp";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function Stat({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-[0.15em] text-white/60">{label}</p>
      <p className={`mt-0.5 font-heading text-base font-bold ${accent || "text-white"}`}>{value}</p>
    </div>
  );
}

export default function PublicPortfolio() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [deck, setDeck] = useState([]);
  const [results, setResults] = useState([]); // {product, decision}
  const [drag, setDrag] = useState({ x: 0, active: false });
  const startX = useRef(0);

  useEffect(() => {
    axios.get(`${API}/public/product-shares/${token}`)
      .then((r) => { setData(r.data); setDeck(r.data.products); })
      .catch(() => setError("This portfolio link is invalid or has been removed."));
  }, [token]);

  const top = deck[0];

  const decide = (product, decision) => {
    setResults((r) => [...r, { product, decision }]);
    setDeck((d) => d.slice(1));
    setDrag({ x: 0, active: false });
    axios.post(`${API}/public/product-shares/${token}/decision`, { product_id: product.id, decision }).catch(() => {});
  };

  const undo = () => {
    const last = results[results.length - 1];
    if (!last) return;
    setResults((r) => r.slice(0, -1));
    setDeck((d) => [last.product, ...d]);
  };

  const onDown = (e) => { startX.current = e.clientX; setDrag({ x: 0, active: true }); e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (drag.active) setDrag({ x: e.clientX - startX.current, active: true }); };
  const onUp = () => {
    if (!drag.active) return;
    if (drag.x > 110) decide(top, "approved");
    else if (drag.x < -110) decide(top, "rejected");
    else setDrag({ x: 0, active: false });
  };

  if (error) return <div className="flex h-screen items-center justify-center bg-zinc-950 px-6 text-center text-sm text-zinc-400">{error}</div>;
  if (!data) return <div className="flex h-screen items-center justify-center bg-zinc-950"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>;

  const approved = results.filter((r) => r.decision === "approved").length;
  const rejected = results.filter((r) => r.decision === "rejected").length;

  return (
    <div className="flex min-h-screen flex-col bg-zinc-950">
      <header className="border-b border-white/10 px-6 py-5">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-[#e84e34] p-1.5"><img src={logoWhite} alt="Frdm BizOps" className="h-full w-auto object-contain" /></div>
          <div className="flex-1">
            <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#f3897a]">Frdm BizOps</p>
            <h1 className="font-heading text-xl font-bold tracking-tight text-white" data-testid="portfolio-title">{data.title}</h1>
          </div>
          <div className="text-right text-xs text-zinc-400">
            <span className="text-emerald-400">{approved} approved</span> · <span className="text-[#f3897a]">{rejected} rejected</span>
          </div>
        </div>
      </header>

      <main className="flex flex-1 flex-col items-center justify-center px-6 py-8">
        {deck.length === 0 ? (
          <div className="text-center" data-testid="portfolio-complete">
            <p className="font-heading text-2xl font-bold text-white">All reviewed 🎉</p>
            <p className="mt-2 text-sm text-zinc-400">{approved} approved · {rejected} rejected</p>
            {results.length > 0 && <button onClick={undo} data-testid="portfolio-undo" className="mt-6 inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-sm text-white hover:border-[#e84e34]"><Undo2 className="h-4 w-4" /> Undo last</button>}
          </div>
        ) : (
          <>
            <div className="relative h-[520px] w-full max-w-sm" data-testid="portfolio-deck">
              {deck.slice(0, 3).map((p, i) => {
                const isTop = i === 0;
                const imgUrl = p.has_image ? `${API}/public/product-shares/${token}/image/${p.id}` : null;
                const marginNeg = p.margin != null && p.margin < 0;
                const style = isTop
                  ? { transform: `translateX(${drag.x}px) rotate(${drag.x / 20}deg)`, transition: drag.active ? "none" : "transform 0.3s" }
                  : { transform: `scale(${1 - i * 0.04}) translateY(${i * 12}px)` };
                return (
                  <div
                    key={p.id}
                    data-testid={isTop ? "portfolio-top-card" : undefined}
                    className="absolute inset-0 select-none overflow-hidden rounded-2xl bg-zinc-800 shadow-2xl ring-1 ring-white/10"
                    style={{ ...style, zIndex: 10 - i, touchAction: "none" }}
                    onPointerDown={isTop ? onDown : undefined}
                    onPointerMove={isTop ? onMove : undefined}
                    onPointerUp={isTop ? onUp : undefined}
                  >
                    {imgUrl ? <img src={imgUrl} alt={p.name} draggable={false} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-zinc-700 to-zinc-900"><Package className="h-14 w-14 text-white/20" /></div>}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10" />
                    {isTop && drag.x > 40 && <div className="absolute left-5 top-5 rotate-[-12deg] border-4 border-emerald-400 px-3 py-1 font-heading text-2xl font-black uppercase text-emerald-400">Approve</div>}
                    {isTop && drag.x < -40 && <div className="absolute right-5 top-5 rotate-[12deg] border-4 border-[#f3897a] px-3 py-1 font-heading text-2xl font-black uppercase text-[#f3897a]">Reject</div>}
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="font-heading text-2xl font-bold leading-tight text-white drop-shadow">{p.name}</h3>
                      {p.description && <p className="mt-1 line-clamp-2 text-xs text-white/70">{p.description}</p>}
                      <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/15 pt-3">
                        <Stat label="RRP" value={money(p.rrp)} />
                        <Stat label="Cost" value={money(p.cost)} />
                        <Stat label="Profit" value={money(p.profit)} />
                        <Stat label="Margin" value={p.margin != null ? `${p.margin.toFixed(1)}%` : "—"} accent={p.margin == null ? "text-white" : marginNeg ? "text-[#f3897a]" : "text-emerald-400"} />
                      </div>
                      {p.units > 1 && <p className="mt-2 text-[11px] text-white/60">{p.units} units · {money(p.rrp_per_unit)}/unit RRP · {money(p.cost_per_unit)}/unit cost</p>}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex items-center gap-6">
              <button onClick={() => decide(top, "rejected")} data-testid="portfolio-reject" className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#f3897a] text-[#f3897a] transition-transform hover:scale-110"><X className="h-7 w-7" /></button>
              {results.length > 0 && <button onClick={undo} data-testid="portfolio-undo" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white/70 transition-transform hover:scale-110"><Undo2 className="h-5 w-5" /></button>}
              <button onClick={() => decide(top, "approved")} data-testid="portfolio-approve" className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-emerald-400 text-emerald-400 transition-transform hover:scale-110"><Check className="h-7 w-7" /></button>
            </div>
            <p className="mt-5 text-xs text-zinc-500">Swipe or tap · {deck.length} left</p>
          </>
        )}
      </main>
    </div>
  );
}
